import type { BoardListItem } from '@hygge/shared';
import type { ArchiveBoardChoice } from '@/lib/projects';
import { cn } from '@/lib/utils';
import { sortProjectBoards } from '../lib/project-boards';

/**
 * The boards' half of archiving a project (HW-983). It is handed, and lists,
 * the linked boards whose settings the reader may change — the ones the server
 * applies the choice to; every other linked board is left as it is, for its
 * own admins to decide.
 */
export function ArchiveBoardsChoice({
  boards,
  value,
  onChange,
  loadFailed = false,
}: {
  boards: BoardListItem[];
  value: ArchiveBoardChoice | null;
  onChange: (value: ArchiveBoardChoice) => void;
  /** The boards could not be loaded: say so, rather than archive without asking. */
  loadFailed?: boolean;
}) {
  if (loadFailed) {
    return (
      <p className="text-sm text-destructive text-left">
        The project's boards could not be loaded, so it cannot be asked what becomes of them. Try again.
      </p>
    );
  }
  if (boards.length === 0) return null;

  const sorted = sortProjectBoards(boards);
  const one = sorted.length === 1;
  const options: { value: ArchiveBoardChoice; title: string; hint: string }[] = [
    {
      value: 'archive',
      title: one ? 'Archive the board too' : 'Archive the boards too',
      hint: one ? 'It is marked Archived and stays linked.' : 'The boards listed are marked Archived and stay linked.',
    },
    {
      value: 'unlink',
      title: one ? 'Keep the board active and unlink it' : 'Keep the boards active and unlink them',
      hint: one ? 'It stays as it is, with no project.' : 'They stay as they are, with no project.',
    },
  ];

  return (
    <fieldset className="space-y-2 text-left">
      <legend className="text-sm font-medium text-foreground mb-1">
        {sorted.length === 1 ? 'A board you manage is' : `${sorted.length} boards you manage are`} linked to this
        project:{' '}
        <span className="font-normal text-muted-foreground">{sorted.map((b) => b.name).join(', ')}</span>
      </legend>
      {options.map((option) => (
        <label
          key={option.value}
          className={cn(
            'flex items-start gap-2.5 rounded-md border p-2.5 cursor-pointer transition-colors',
            value === option.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
          )}
        >
          <input
            type="radio"
            name="archive-boards"
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className="mt-0.5 accent-[var(--color-primary)] cursor-pointer"
          />
          <span>
            <span className="block text-sm text-foreground">{option.title}</span>
            <span className="block text-xs text-muted-foreground">{option.hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}
